import { Link } from "react-router-dom";
import { ChevronRight, Cpu, Monitor, CreditCard, Building2, Phone, MapPin, ShieldCheck, Sparkles, MessageCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import { BRAND, WHATSAPP_LINK } from "../lib/brand";
import CTAStrip from "../components/CTAStrip";

const OFFERS = [
  { icon: Cpu, title: "DigiPOS Software Suite", text: "Billing, inventory, accounting, GST, multi-branch. Online, offline & Android." },
  { icon: Monitor, title: "POS Hardware", text: "Touch POS terminals, thermal printers, barcode scanners, cash drawers, accessories." },
  { icon: CreditCard, title: "RFID & Smart Cards", text: "MIFARE cards, membership cards, access control & identification solutions." },
  { icon: Building2, title: "Amusement & Ticketing", text: "Specialized solutions for parks, entertainment & recreational facilities." },
];

const INDUSTRIES = [
  "Supermarkets", "Hypermarkets", "Grocery & Mini Markets", "Textiles", "Garment Stores", "Footwear & Fashion",
  "Mobile Phone & Electronics", "Computer & IT Retail", "Hardware Stores", "Paint & Electrical", "Fancy Stores",
  "Stationery", "Sports & Fitness", "Jewellery", "Cosmetics & Beauty", "Pharmacies", "Medical Stores", "Bakeries",
  "Restaurants", "Cafés", "Food Courts", "Ice Cream Parlours", "Wholesale", "Distribution", "Automobile Spare Parts",
  "Home Appliances", "Pet Shops", "Agricultural Supply", "Amusement Parks", "Membership Orgs (RFID)",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative gi-hero-gradient text-white pt-32 pb-20">
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-white">About Us</span>
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">
              About Gravityinc Business Solutions
            </h1>
            <p className="text-white/75 mt-5 max-w-2xl text-lg">
              12 years of powering retail and distribution businesses across India with in-house built technology.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest text-[#1565C0]">Our Story</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 mb-6">
              Built in Kochi. Trusted across India.
            </h2>
            <div className="space-y-4 text-[#1A1A2E]/85 leading-relaxed">
              <p>
                Gravityinc Business Solutions is a leading Retail Automation and POS Solutions provider with over <strong>12 years</strong> of industry experience, headquartered in Panampilly Nagar, Kochi.
              </p>
              <p>
                We specialize in complete billing, inventory management and business automation solutions for retailers and service businesses across every industry — from supermarkets and pharmacies to amusement parks.
              </p>
              <p>
                Our flagship product, <strong>DigiPOS</strong>, is entirely in-house developed billing and retail management software trusted by thousands of businesses. Available in Online (Cloud), Offline (Desktop) and Android-based versions.
              </p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#1565C0]/20 to-[#00B4D8]/20 blur-3xl rounded-3xl" />
              <div className="relative rounded-2xl overflow-hidden border border-[#DDE6F0] shadow-xl bg-[#F5F8FF] p-10">
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[
                    { n: "12+", l: "Years" },
                    { n: "3K+", l: "Businesses" },
                    { n: "50+", l: "Industries" },
                  ].map((s) => (
                    <div key={s.l} className="bg-white rounded-xl p-5 border border-[#DDE6F0]">
                      <div className="font-display font-bold text-2xl text-[#1565C0]">{s.n}</div>
                      <div className="text-xs text-[#6B7280] mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-[#0B2545] to-[#1565C0] text-white">
                  <Sparkles className="h-5 w-5 text-[#00B4D8] mb-3" />
                  <div className="font-display font-semibold text-lg leading-tight">In-house built. Locally supported.</div>
                  <div className="text-white/70 text-sm mt-1">Engineers, support & implementation — all from Kochi.</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest text-[#1565C0]">Mission</span>
            <p className="font-display font-medium text-2xl md:text-3xl mt-4 leading-snug text-[#0B2545]">
              {`"To provide reliable, innovative and cost-effective business automation solutions backed by professional implementation, training and after-sales support."`}
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {["Reliability", "Innovation", "Affordability"].map((v) => (
                <span key={v} className="px-5 py-2 rounded-full bg-white border border-[#DDE6F0] text-sm font-medium text-[#1565C0]">
                  {v}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3 max-w-3xl">Everything your business needs, from one partner</h2>
            <p className="text-[#6B7280] max-w-2xl">From software to hardware to consumables — Gravityinc is your one-stop retail tech partner.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {OFFERS.map((o, i) => (
              <Reveal key={o.title} delay={i * 70}>
                <div className="gi-card gi-card-hover p-7 h-full">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1565C0] to-[#00B4D8] grid place-items-center text-white mb-4">
                    <o.icon className="h-5 w-5" />
                  </div>
                  <h4 className="font-display font-semibold text-lg mb-2">{o.title}</h4>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{o.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-8 flex flex-wrap items-center gap-3 p-5 rounded-xl border border-[#DDE6F0] bg-[#F5F8FF]">
              <span className="text-sm font-medium text-[#1565C0]">Consumables we supply:</span>
              {["Thermal Rolls", "Barcode Labels", "Ribbon Rolls"].map((c) => (
                <span key={c} className="px-3 py-1 rounded-full bg-white border border-[#DDE6F0] text-xs">{c}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3">Who We Serve</h2>
            <p className="text-[#6B7280] max-w-2xl">From single-store retail to multi-state distribution networks.</p>
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-10 flex flex-wrap gap-2">
              {INDUSTRIES.map((ind) => (
                <span key={ind} className="px-4 py-2 text-sm rounded-full bg-white border border-[#DDE6F0] hover:border-[#1565C0] hover:text-[#1565C0] transition">
                  {ind}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT CARD */}
      <section className="gi-deep-gradient text-white gi-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <span className="gi-pill-dark mb-4"><ShieldCheck className="h-3.5 w-3.5" /> Reach Us</span>
              <h3 className="font-display font-bold text-2xl md:text-3xl mb-4">Visit, call or message us</h3>
              <div className="space-y-3 text-white/80 text-sm">
                <div className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-[#00B4D8]" /> {BRAND.address}</div>
                <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-[#00B4D8]" /> {BRAND.phone1} · {BRAND.phone2}</div>
                <div className="text-xs text-white/60 mt-3">GST: {BRAND.gst}</div>
              </div>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-btn" className="gi-btn-whatsapp justify-center">
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
              <Link to="/contact" data-testid="about-contact-btn" className="gi-btn-primary justify-center">
                Send Us an Enquiry
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
